import { html, property } from 'lit-element'
import { guard } from 'lit-html/directives/guard'
import { repeat } from 'lit-html/directives/repeat'
import { nothing } from 'lit-html'

import { menuItems } from '../avatar-popover-menu-items/avatar-popover-menu-items'
import { renderNormalMenuItem } from '../menu-item/menu-item-renderer'
import type { MenuItem } from '../menu-item/menu-item-models'
import { MenuItemType } from '../menu-item/menu-item-models'
import avatarPopoverMenuStyles from './avatar-popover-menu.styles.scss'
import { getLaunchDarklyFlag } from '../../services/feature-flags'
import { LaunchDarklyVariations } from '../../services/feature-flags/models'
import type { AvatarSize, FontTokens } from '@getgo/chameleon-core'
import { getShellApiInstance, getShellAnalytics } from '../../common/shell-api-helpers'
import { ShellElement } from '../../common/shell-element'
import type { PresenceNamespace } from '../../services/namespaces/presence-namespace'
import { PRESENCE_NAMESPACE } from '../../services/namespaces/presence-namespace'

export interface AvatarAndName {
  readonly givenName: string
  readonly familyName: string
  readonly profileImageUrl: string
}

// Normally this div should be with role="menuitem" tabindex="-1", but we don't need to announce it
// Since the goto-avatar-container tooltip, avatar and presence indicator will already have announced
const renderAvatarAndName = ({ familyName, givenName, profileImageUrl }: AvatarAndName) =>
  guard([familyName, givenName, profileImageUrl], () => {
    const externalUserKey = getShellApiInstance().user.key ?? ''
    const hasAdvancedPresenceFlag = !!getLaunchDarklyFlag(LaunchDarklyVariations.SHELL_ADVANCED_PRESENCE)
    let avatarSize: AvatarSize | undefined
    let typeVariant: keyof FontTokens = 'body-small-semibold'
    let shouldShowPresence = false
    if (hasAdvancedPresenceFlag) {
      avatarSize = 'large'
      typeVariant = 'body-medium-semibold'
      shouldShowPresence = true
    }
    return html`
      <div class="avatar-and-name" aria-hidden="true">
        <goto-avatar
          .showPresence=${shouldShowPresence}
          .externalUserKey=${externalUserKey}
          .familyName=${familyName}
          .givenName=${givenName}
          .profileImageUrl=${profileImageUrl}
          .size=${avatarSize}
          variant="primary"
        ></goto-avatar>
        <div>
          <chameleon-typography variant=${typeVariant}>${givenName} ${familyName}</chameleon-typography>
          ${hasAdvancedPresenceFlag ? html`<goto-presence-selector-popover></goto-presence-selector-popover>` : nothing}
        </div>
      </div>
    `
  })

const renderUserNoteInput = (clickHandler: () => void): ReturnType<typeof html> | typeof nothing =>
  getLaunchDarklyFlag(LaunchDarklyVariations.SHELL_ADVANCED_PRESENCE)
    ? html` <goto-user-note-input @click=${clickHandler}></goto-user-note-input>`
    : nothing

const renderMenuItemDndSwitch = (): ReturnType<typeof html> | typeof nothing =>
  getLaunchDarklyFlag(LaunchDarklyVariations.SHELL_AVATAR_POPOVER_DND_MENU_ITEM) &&
  !getLaunchDarklyFlag(LaunchDarklyVariations.SHELL_ADVANCED_PRESENCE)
    ? html`
        <chameleon-menu-separator></chameleon-menu-separator>
        <goto-avatar-popover-menu-item-switch></goto-avatar-popover-menu-item-switch>
      `
    : nothing

const renderMenuItemTemplate = (item: MenuItem): ReturnType<typeof html> | typeof nothing => {
  const isVisible = item.isVisible?.() ?? true

  if (isVisible) {
    switch (item.type) {
      case MenuItemType.SEPARATOR:
        return html`<chameleon-menu-separator></chameleon-menu-separator>`
      case MenuItemType.CUSTOM:
        return item.render()
      case MenuItemType.NORMAL:
        return renderNormalMenuItem(item)
    }
  }
  return nothing
}

export class GoToAvatarPopoverMenu extends ShellElement implements AvatarAndName {
  static readonly tagName = 'goto-avatar-popover-menu'
  // TODO remove non null assertion and fix type https://jira.ops.expertcity.com/browse/SCAPI-536
  @property({ type: String }) givenName = ''
  @property({ type: String }) familyName = ''
  @property({ type: String }) profileImageUrl = ''

  static get styles() {
    return avatarPopoverMenuStyles
  }

  render() {
    return html`
      <chameleon-menu>
        ${renderAvatarAndName(this)} ${renderUserNoteInput(this.handleOpenEditUserNoteDialog.bind(this))}
        ${renderMenuItemDndSwitch()} ${repeat(menuItems, renderMenuItemTemplate)}
        <chameleon-menu-separator></chameleon-menu-separator>
        <chameleon-menu-section>
          <goto-version></goto-version>
        </chameleon-menu-section>
      </chameleon-menu>
    `
  }

  private handleOpenEditUserNoteDialog() {
    if (getLaunchDarklyFlag(LaunchDarklyVariations.SHELL_ADVANCED_PRESENCE)) {
      // Mixpanel; Open Custom Status modal
      getShellAnalytics().track('GoTo > Custom Status Modal', { action: 'Click', event: 'Open', from: 'Avatar menu' })

      getShellApiInstance()
        .namespaces.retrieve<PresenceNamespace>(PRESENCE_NAMESPACE)
        .commands.openPresenceNoteDialog.execute()
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    readonly [GoToAvatarPopoverMenu.tagName]: GoToAvatarPopoverMenu
  }
}
